import { motion } from "framer-motion";
import { ArrowRight, Brain, Eye, Network, Sparkles, Zap } from "lucide-react";
import { CURRENT_TIERS, RECOMMENDED_TIERS, SPECIALISTS, AI_ROUTER_AGENTS, LITELLM_FACTS } from "../data/aiStack";
import { D3SankeyChart, type SankeyData } from "../components/charts/D3SankeyChart";

// Mock routing weights: % of traffic each tier sends to each model.
// Numbers are illustrative until LangFuse routing data is wired in.
const TIER_MODEL_WEIGHTS: Array<{ tier: number; weight: number; models: Array<{ name: string; share: number }> }> = [
  { tier: 0, weight: 20, models: [{ name: "llama3.2", share: 0.5 }, { name: "deepseek-coder-v2:16b", share: 0.35 }, { name: "qwen2.5-coder:14b", share: 0.15 }] },
  { tier: 1, weight: 5,  models: [{ name: "llama-3.1-8b:free", share: 1.0 }] },
  { tier: 2, weight: 45, models: [{ name: "gpt-4o-mini", share: 0.55 }, { name: "gemini-flash-1.5", share: 0.3 }, { name: "llama-3.3-70b", share: 0.15 }] },
  { tier: 3, weight: 25, models: [{ name: "claude-sonnet-4-6", share: 0.6 }, { name: "gpt-4o", share: 0.25 }, { name: "gemini-pro-1.5", share: 0.15 }] },
  { tier: 4, weight: 5,  models: [{ name: "claude-opus-4-7", share: 0.7 }, { name: "o3-mini", share: 0.3 }] },
];

function buildSankey(): SankeyData {
  const nodes: SankeyData["nodes"] = [];
  const links: SankeyData["links"] = [];
  const modelIndex = new Map<string, number>();

  TIER_MODEL_WEIGHTS.forEach((t) => {
    const tierMeta = CURRENT_TIERS.find((c) => c.level === t.tier);
    nodes.push({
      name: `T${t.tier} · ${tierMeta?.name ?? ""}`,
      color: tierMeta?.color,
    });
  });

  TIER_MODEL_WEIGHTS.forEach((t, ti) => {
    t.models.forEach((m) => {
      let mi = modelIndex.get(m.name);
      if (mi === undefined) {
        mi = nodes.length;
        modelIndex.set(m.name, mi);
        nodes.push({ name: m.name, color: "#94a3b8" });
      }
      const value = Math.max(1, Math.round(t.weight * m.share * 10));
      links.push({ source: ti, target: mi, value });
    });
  });

  return { nodes, links };
}

export default function AIStack() {
  const sankey = buildSankey();
  return (
    <div className="space-y-8">
      <header>
        <div className="eyebrow">Layer · AI</div>
        <h1 className="text-3xl font-bold mt-1 tracking-tight">AI Stack — How It Actually Works</h1>
        <p className="text-slate-400 mt-2 max-w-3xl">
          Three independent systems that often get conflated. ai-router decides <em>what</em> model
          to use. LiteLLM <em>calls</em> the model. LangFuse <em>watches</em> the call. They are not
          wired together yet — that's the next gap.
        </p>
      </header>

      {/* Architecture flow */}
      <div className="glass p-6 ring-glass">
        <div className="eyebrow mb-4">Flow Diagram</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center">
          <FlowNode title="Request" subtitle="user / agent / cron" icon={Sparkles} accent="text-accent-cyan" />
          <FlowArrow />
          <FlowNode title="ai-router" subtitle="picks tier + model" icon={Brain} accent="text-accent-violet" current />
          <FlowArrow muted label="should call" />
          <FlowNode title="LiteLLM" subtitle="proxies provider" icon={Network} accent="text-accent-blue" current />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-center mt-3">
          <div />
          <div />
          <FlowNode title="agents/" subtitle="bypass router today" icon={Zap} accent="text-accent-amber" warning />
          <FlowArrow muted label="bypass" />
          <FlowNode title="OpenRouter" subtitle="actual provider" icon={Network} accent="text-slate-400" />
        </div>
        <div className="mt-4 pt-4 border-t border-white/[0.04] text-xs text-slate-500 font-mono">
          Reality: ai-router returns metadata. Agent workers ignore it and call OpenRouter directly. LiteLLM is up-and-running but receives no internal traffic.
        </div>
      </div>

      {/* Tiers comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TierColumn
          title="Currently deployed"
          subtitle="7 tiers · overlapping"
          tiers={CURRENT_TIERS}
          accent="text-accent-amber"
        />
        <TierColumn
          title="Recommended"
          subtitle="4 tiers · no overlap"
          tiers={RECOMMENDED_TIERS}
          accent="text-accent-emerald"
        />
      </div>

      {/* Specialists */}
      <div className="glass p-6 ring-glass">
        <div className="eyebrow">Specialists (flag, not tier)</div>
        <h2 className="text-xl font-semibold mt-1">Task-specific overrides</h2>
        <p className="text-sm text-slate-400 mt-1 max-w-3xl">
          Reserved for tasks where a specific capability beats general intelligence. Treat as
          a flag on the request, not a separate price tier — that was the source of bloat.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {SPECIALISTS.map((s) => (
            <div key={s.id} className="px-4 py-3 rounded-xl bg-white/[0.02] border border-white/[0.04]">
              <div className="text-sm font-mono text-accent-cyan">{s.id}</div>
              <div className="text-xs text-slate-400 mt-1">{s.capability}</div>
              <div className="mt-2 flex flex-wrap gap-1">
                {s.models.slice(0, 3).map((m) => (
                  <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-300 font-mono">{m}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Component cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ComponentCard
          title="ai-router"
          subtitle="Cloudflare Worker"
          accent="text-accent-violet"
          icon={Brain}
          stats={[
            { k: "Agents",        v: String(AI_ROUTER_AGENTS.total) },
            { k: "Tiers",         v: "7 (deployed)" },
            { k: "Categories",    v: Object.keys(AI_ROUTER_AGENTS.categories).length.toString() },
            { k: "Calls models?", v: "No — recommendation only" },
          ]}
          summary="Stateless catalog + scoring API. Returns the best (modelId, tier) for a given task; the caller does the actual fetch."
        />
        <ComponentCard
          title="LiteLLM"
          subtitle="Azure Container App"
          accent="text-accent-blue"
          icon={Network}
          stats={[
            { k: "Registered",  v: String(LITELLM_FACTS.registeredModels) },
            { k: "Direct keys", v: String(LITELLM_FACTS.directKeyModels) },
            { k: "Routed",      v: String(LITELLM_FACTS.openRouterRoutedModels) },
            { k: "Status",      v: "Healthy · 4h uptime" },
          ]}
          summary="One OpenAI-compatible endpoint for every provider. Most '99 models' are openrouter/* wildcard expansions."
        />
        <ComponentCard
          title="LangFuse"
          subtitle="Azure Container App"
          accent="text-accent-cyan"
          icon={Eye}
          stats={[
            { k: "Version", v: "2.95.11" },
            { k: "Source",  v: "LiteLLM only" },
            { k: "n8n",     v: "Not wired" },
            { k: "Workers", v: "Not wired" },
          ]}
          summary="Trace + cost capture. Currently sees LiteLLM traffic only. n8n, ai-router workers, and direct Ollama calls don't report."
        />
      </div>

      {/* Tier → Model flow */}
      <div className="glass p-6 ring-glass">
        <div className="eyebrow">Routing · Mock Volumes</div>
        <h2 className="text-xl font-semibold mt-1">Tier → Model flow</h2>
        <p className="text-sm text-slate-400 mt-1 max-w-3xl">
          Illustrative routing share from each tier (left) into the actual model
          executed (right). Replace with LangFuse-derived counts once it captures the full
          surface.
        </p>
        <div className="mt-4 -mx-2 overflow-x-auto">
          <div className="min-w-[860px] px-2">
            <D3SankeyChart data={sankey} width={860} height={420} />
          </div>
        </div>
      </div>
    </div>
  );
}

function FlowNode({ title, subtitle, icon: Icon, accent, current, warning }:
  { title: string; subtitle: string; icon: any; accent: string; current?: boolean; warning?: boolean; }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative px-4 py-4 rounded-2xl border ${current ? "border-accent-violet/40 bg-accent-violet/5" : "border-white/[0.06] bg-white/[0.02]"} text-center`}
    >
      <div className={`mx-auto w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center mb-2 ${accent}`}>
        <Icon size={18} />
      </div>
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-[11px] font-mono text-slate-500 mt-0.5">{subtitle}</div>
      {warning && <div className="absolute -top-2 right-2 px-1.5 py-0.5 rounded bg-accent-amber/20 text-[9px] font-mono text-accent-amber">bypass</div>}
    </motion.div>
  );
}
function FlowArrow({ muted, label }: { muted?: boolean; label?: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <ArrowRight size={20} className={muted ? "text-slate-700" : "text-accent-blue"} />
      {label && <span className="text-[9px] font-mono text-slate-500">{label}</span>}
    </div>
  );
}
function TierColumn({ title, subtitle, tiers, accent }:
  { title: string; subtitle: string; tiers: typeof CURRENT_TIERS; accent: string; }) {
  return (
    <div className="glass p-6 ring-glass">
      <div className="eyebrow">Configuration</div>
      <h2 className={`text-xl font-semibold mt-1 ${accent}`}>{title}</h2>
      <div className="text-xs font-mono text-slate-500 mt-0.5">{subtitle}</div>
      <div className="mt-4 space-y-2">
        {tiers.map((t) => (
          <div key={t.level} className={`px-3 py-3 rounded-lg border ${t.recommended ? "border-white/[0.08] bg-white/[0.03]" : "border-white/[0.04] bg-white/[0.01] opacity-60"}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-md text-xs font-bold flex items-center justify-center" style={{ background: t.color + "22", color: t.color }}>
                  {t.level}
                </span>
                <span className="text-sm font-semibold">{t.name}</span>
              </div>
              <span className="text-[11px] font-mono text-slate-500">{t.costCeiling}</span>
            </div>
            <div className="text-xs text-slate-400 mt-1">{t.intent}</div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {t.models.slice(0, 4).map((m) => (
                <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.03] text-slate-400 font-mono">{m}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
function ComponentCard({ title, subtitle, accent, icon: Icon, stats, summary }:
  { title: string; subtitle: string; accent: string; icon: any;
    stats: Array<{ k: string; v: string }>; summary: string; }) {
  return (
    <div className="glass p-6 ring-glass">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center ${accent}`}>
          <Icon size={18} />
        </div>
        <div>
          <div className="text-base font-semibold">{title}</div>
          <div className="eyebrow">{subtitle}</div>
        </div>
      </div>
      <p className="mt-4 text-sm text-slate-400 leading-relaxed">{summary}</p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        {stats.map((s) => (
          <div key={s.k} className="px-2.5 py-2 rounded-lg bg-white/[0.02]">
            <div className="eyebrow text-[9px]">{s.k}</div>
            <div className="text-xs font-mono text-slate-200 mt-1 truncate">{s.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
